import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { nivelTone } from '../data';
import type { DiagnosticoListItem, Nivel } from '../schemas';

interface Props {
  items: DiagnosticoListItem[];
  search: string;
  nivelFilter: Nivel | '';
  onSelect: (cpf: string) => void;
}

export function DiagnosticoTable({ items, search, nivelFilter, onSelect }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (nivelFilter && it.nivel !== nivelFilter) return false;
      if (!q) return true;
      return (
        it.nome.toLowerCase().includes(q) ||
        it.cpf.includes(q) ||
        (it.municipio ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, search, nivelFilter]);

  const columns = useMemo<ColumnDef<DiagnosticoListItem>[]>(
    () => [
      {
        id: 'nome',
        accessorKey: 'nome',
        header: 'Nome',
        cell: ({ row }) => (
          <span className="text-sm text-text truncate">{row.original.nome}</span>
        ),
      },
      {
        id: 'municipio',
        accessorKey: 'municipio',
        header: 'Município',
        cell: ({ getValue }) => (
          <span className="text-sm text-text-2">{(getValue() as string | null) || '—'}</span>
        ),
      },
      {
        id: 'score',
        accessorKey: 'score',
        header: 'Score',
        cell: ({ row }) => {
          const s = row.original.score;
          const tone = nivelTone(row.original.nivel);
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-text tabular-nums w-10">
                {s.toFixed(0)}%
              </span>
              <div className="h-1 w-20 rounded-full bg-surface overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${s}%`,
                    backgroundColor:
                      tone === 'ok'
                        ? 'var(--ok)'
                        : tone === 'warn'
                          ? 'var(--warning)'
                          : 'var(--danger)',
                  }}
                />
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden">
      <table className="ds-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  onClick={
                    h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined
                  }
                  className={h.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {h.column.getIsSorted() === 'asc' && ' ↑'}
                  {h.column.getIsSorted() === 'desc' && ' ↓'}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row.original.cpf)}
              className="cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-text-muted">
          Nenhum cliente encontrado.
        </div>
      )}
    </div>
  );
}
